import { logWarning } from 'lib/util/errors';
import fnNoOp from 'lib/util/no-op';
import { useEffect, useState } from 'react';

export type WebpFeature = 'lossy' | 'lossless' | 'alpha' | 'animation';

/**
 * Callback function used by detection hooks to pass information back to component.
 * @param {boolean} supported True if the tested feature is supported, otherwise false.
 * @param {WebpFeature} feature The feature that was tested.
 * @param {(() => void) | undefined} teardown Optional teardown method that can be used to clean up after detection.
 */
export type WebpDetectionCallback = (
  supported: boolean,
  feature: WebpFeature,
  teardown?: () => void
) => void;
/**
 * Returned by a Webp detection hook.
 */
export type WebpDetectionHookResponse = {
  /**
   * Function that can be used to teardown any active feature testing.
   * @returns {void}.
   */
  teardown: () => void;
};
/**
 * Supports custom feature detection hooks (as well as unit testing).
 * @param {WebpFeature} feature The feature the hook should test.
 * @param {WebpDetectionCallback} callback A callback to invoke once testing has completed.
 * @returns {WebpDetectionHookResponse} A teardown value that can be used to cancel and clean up after detection tests.
 */
export type WebpDetectionHook = (
  feature: WebpFeature,
  callback: WebpDetectionCallback
) => WebpDetectionHookResponse;
/**
 * Webp detection and fallback component properties.
 */
export type WebpWithFallbackProps = {
  /**
   * The @see {WebpFeature} the component will test for.
   */
  feature: WebpFeature;
  /**
   * The class name appended to the document if testing fails.  If not set, a default value of legacy-images is used.
   */
  className?: string;
  /**
   * Override default detection functionality with a custom callback.
   */
  detectionHook?: WebpDetectionHook;
};

/**
 * Check to see if the current browser has webp support.
 * @see https://developers.google.com/speed/webp/faq#how_can_i_detect_browser_support_for_webp
 */
const checkWebpFeature = (
  feature: WebpFeature,
  callback: (supported: boolean, feature: WebpFeature) => void
) => {
  const kTestImages = {
    lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
    lossless: 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==',
    alpha:
      'UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==',
    animation:
      'UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA',
  };
  let img: HTMLImageElement | undefined = new Image();
  const onload = () => {
    if (!img) {
      return;
    }
    const result = img.width > 0 && img.height > 0;
    callback(result, feature);
  };
  const onerror = () => {
    if (!img) {
      return;
    }
    callback(false, feature);
  };
  const teardown = () => {
    const myImg = img;
    img = undefined;
    if (myImg) {
      myImg.removeEventListener('error', onerror);
      myImg.removeEventListener('load', onload);
      myImg.remove();
    }
  };
  img.addEventListener('load', onload);
  img.addEventListener('error', onerror);
  img.src = `data:image/webp;base64,${kTestImages[feature]}`;
  return {
    teardown,
  };
};

const WebpWithFallback = ({
  feature = 'lossy',
  className = 'legacy-images',
  detectionHook,
}: WebpWithFallbackProps) => {
  const [isSupported, setIsSupported] = useState<boolean | undefined>();
  useEffect(() => {
    // If we have already tested for support we have nothing to do
    if (typeof isSupported !== 'undefined') {
      return fnNoOp;
    }
    // Use detection hook to see if we support the requested feature.
    const { teardown } = (detectionHook ?? checkWebpFeature)(
      feature,
      (supported: boolean, f: WebpFeature, td?: () => void) => {
        // Teardown webp feature detection events
        (td ?? teardown)();
        // If webp support is not available tack a legacy-images class onto the page
        if (!supported) {
          logWarning(`Device does not have webp ${feature} support`);
          if (document.body.className.indexOf(className) === -1) {
            document.body.className += ` ${className}`;
          }
        }
        setIsSupported(supported);
      }
    );
    return () => {
      teardown();
    };
  }, [isSupported, feature, className, detectionHook]);

  return <></>;
};
export default WebpWithFallback;
